/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { ExportTemplateDTO } from '../schemas/exportTemplateDTO.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Inserisce o aggiorna un template utente
 * @summary Inserisce o aggiorna un template utente
 */
export const postBackExportTemplate = (
  exportTemplateDTO: ExportTemplateDTO,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ExportTemplateDTO>(
    {
      url: `/framework-bo-procedimenti/export-template`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: exportTemplateDTO,
    },
    options,
  );
};

export const getPostBackExportTemplateMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackExportTemplate>>,
    TError,
    { data: ExportTemplateDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackExportTemplate>>,
  TError,
  { data: ExportTemplateDTO },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackExportTemplate>>,
    { data: ExportTemplateDTO }
  > = props => {
    const { data } = props ?? {};

    return postBackExportTemplate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackExportTemplateMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackExportTemplate>>
>;
export type PostBackExportTemplateMutationBody = ExportTemplateDTO;
export type PostBackExportTemplateMutationError = ErrorType<unknown>;

/**
 * @summary Inserisce o aggiorna un template utente
 */
export const usePostBackExportTemplate = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackExportTemplate>>,
    TError,
    { data: ExportTemplateDTO },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostBackExportTemplateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ritorna l'elenco dei template dell'utente/ufficio
 * @summary Ritorna l'elenco dei template dell'utente/ufficio
 */
export const getBackExportTemplateType = (
  type: 'EXPORT_PROCEDIMENTI_CSV' | 'EXPORT_ELENCHI_CSV',
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ExportTemplateDTO[]>(
    {
      url: `/framework-bo-procedimenti/export-template/${type}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackExportTemplateTypeQueryKey = (
  type: 'EXPORT_PROCEDIMENTI_CSV' | 'EXPORT_ELENCHI_CSV',
) => [`/framework-bo-procedimenti/export-template/${type}`] as const;

export const getGetBackExportTemplateTypeQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackExportTemplateType>>,
  TError = ErrorType<unknown>,
>(
  type: 'EXPORT_PROCEDIMENTI_CSV' | 'EXPORT_ELENCHI_CSV',
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackExportTemplateType>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackExportTemplateType>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackExportTemplateTypeQueryKey(type);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackExportTemplateType>>
  > = ({ signal }) => getBackExportTemplateType(type, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!type, ...queryOptions };
};

export type GetBackExportTemplateTypeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackExportTemplateType>>
>;
export type GetBackExportTemplateTypeQueryError = ErrorType<unknown>;

/**
 * @summary Ritorna l'elenco dei template dell'utente/ufficio
 */
export const useGetBackExportTemplateType = <
  TData = Awaited<ReturnType<typeof getBackExportTemplateType>>,
  TError = ErrorType<unknown>,
>(
  type: 'EXPORT_PROCEDIMENTI_CSV' | 'EXPORT_ELENCHI_CSV',
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackExportTemplateType>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackExportTemplateTypeQueryOptions(type, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Cancella un template utente
 * @summary Cancella un template utente
 */
export const deleteBackExportTemplateId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-bo-procedimenti/export-template/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteBackExportTemplateIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackExportTemplateId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteBackExportTemplateId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteBackExportTemplateId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteBackExportTemplateId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteBackExportTemplateIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteBackExportTemplateId>>
>;

export type DeleteBackExportTemplateIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancella un template utente
 */
export const useDeleteBackExportTemplateId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackExportTemplateId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteBackExportTemplateIdMutationOptions(options);

  return useMutation(mutationOptions);
};
